import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isStringValid, isEmailValid } from 'shared/lib/validators';
import ApiClient from 'shared/lib/ApiClient';
import useForm from 'shared/hooks/useForm';
import Alert from 'shared/components/Alert';
import TranslateError from 'shared/components/TranslateError';
import routes from 'shared/routes';
import { Input, Submit } from 'shared/components/Form';
import styles from './PasswordReset.module.scss';

const PasswordReset = ({ apiClient, app, hash, className }) => {
  const { t } = useTranslation('common');
  const [hashError, setHashError] = useState(null);
  const [state, setState] = useState({ processing: false, sent: false, error: null });

  const checkHash = async () => {
    try {
      await apiClient.get(`/auth/reset-password/${hash}`);
    } catch (err) {
      setHashError(err.error);
    }
  };

  useEffect(() => {
    if (hash) {
      checkHash();
    }
  }, []);

  const onSubmit = async ({ email, newPassword, newPasswordCheck }) => {
    setState({ processing: true, sent: false, error: null });
    try {
      if (hash) {
        await apiClient.post('/auth/reset-password', { hash, newPassword, newPasswordCheck });
      } else {
        await apiClient.post('/auth/reset-password-request', { email });
      }
      setState({ processing: false, sent: true, error: null });
    } catch (err) {
      setState({ processing: false, sent: false, error: err.error });
    }
  };

  const validate = ({ email, newPassword, newPasswordCheck }) => {
    const err = {};

    if (!hash && !isEmailValid(email)) {
      err.email = t('validation.fillValidEmail');
    }

    if (hash && !isStringValid(newPassword)) {
      err.newPassword = t('validation.fillPassword');
    }

    if (hash && newPassword !== newPasswordCheck) {
      err.newPassword = true;
      err.newPasswordCheck = t('validation.passwordsDoesNotMatch');
    }

    return err;
  };

  const { values, handleChange, handleSubmit, resetErrors, errors, hasError } = useForm(onSubmit, {}, validate, { resetOnSubmit: true, scrollToTop: false });

  const onPasswordChange = (event) => {
    handleChange(event);
    resetErrors(['newPassword', 'newPasswordCheck']);
  };

  if (hashError) {
    return (
      <Alert variant="error"><TranslateError>{hashError}</TranslateError></Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
      {state.error && (<Alert variant="error"><TranslateError>{state.error}</TranslateError></Alert>)}
      {state.sent && (<Alert variant="success">{t(hash ? 'passwordReset.done' : 'passwordReset.sent')}</Alert>
      )}
      {hash && state.sent && (
        <p className={styles.login}>
          <Link to={routes[app].login}>{t('button.loginNow')}</Link>
        </p>
      )}
      {hash && !state.sent && (
        <>
          <Input
            type="password"
            name="newPassword"
            label={t('form.newPassword')}
            value={values.newPassword}
            onChange={onPasswordChange}
            error={errors.newPassword}
          />
          <Input
            type="password"
            name="newPasswordCheck"
            label={t('form.newPasswordCheck')}
            value={values.newPasswordCheck}
            onChange={onPasswordChange}
            error={errors.newPasswordCheck}
          />
          <Submit
            title={t('button.changePassword')}
            disabled={hasError}
            onClick={handleSubmit}
            processing={state.processing}
          />
        </>
      )}

      {!hash && (
        <>
          <Input error={errors.email} iconClass="la la-at" name="email" value={values.email} type="email" onChange={handleChange} label={t('form.email')} />
          <Submit
            title={t('button.send')}
            disabled={hasError}
            onClick={handleSubmit}
            processing={state.processing}
          />
        </>
      )}
    </form>
  );
};

PasswordReset.propTypes = {
  apiClient: PropTypes.instanceOf(ApiClient).isRequired,
  app: PropTypes.oneOf(['userportal', 'backoffice']),
  hash: PropTypes.string
};

PasswordReset.defaultProps = {
  app: 'userportal',
  hash: null
};

export default PasswordReset;
